/**
 * Bezugsart der Anfos eines Webinars — Gegenstück zur Tabelle der Montagsmail, aber auf der Sektions-Population
 * (vorläufig ergänzte in eigenem Topf, Listenpreis zum Bestelltag). Nur auf der Sektions-Detailseite
 * (`/api/auswertungen/webinare/:id/bezugsart`, P3-Nachzug, Plan 2026-10-20).
 */
import { FC } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import InfoTip from '../InfoTip';
import { BezugsartAntwort, BezugsartKey } from '../../helpers/auswertungenData';
import { formatEur, formatPct, TERMS } from '../../helpers/kpiDefinitions';
import { formatNumber } from '../../helpers/numberUtils';

const ART: Record<BezugsartKey, string> = {
  upsell: 'Upsell · Jahresbezug',
  quartalsbezug: 'Quartalsbezug',
  einmalkauf: 'Einmalkauf',
  offen: 'Noch offen',
  vorlaeufig: 'Vorläufig ergänzt',
};

const preisText = (r: BezugsartAntwort['rows'][number]): string => {
  if (r.art === 'upsell') return r.preis !== null ? `${formatEur(r.preis, 2)} je Jahr, Zahlung vorab` : 'Jahresbetrag, Zahlung vorab';
  if (r.art === 'quartalsbezug')
    return [
      r.preis !== null ? `${formatEur(r.preis, 2)} je Quartal (${formatEur(r.preis * 4, 2)} im Jahr)` : '',
      r.ohne_testphase ? 'ohne Testphase, Zahlung vorab' : '',
    ]
      .filter(Boolean)
      .join(', ');
  if (r.art === 'offen') return 'Werbeaktion von PL-X noch nicht geliefert';
  if (r.art === 'vorlaeufig') return 'Werbeaktion nur vorläufig bekannt (PX-Ergänzung)';
  return '';
};

const BezugsartTabelle: FC<{ data: BezugsartAntwort }> = ({ data }) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
      Bezugsart
      <InfoTip text={TERMS.bezugsart.tip} />
    </Typography>
    <Table size="small" sx={{ '& td, & th': { px: 1, borderColor: 'divider' }, '& td': { fontVariantNumeric: 'tabular-nums' }, maxWidth: 720 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>Bezugsart</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>
            Anfos
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>
            Anteil
          </TableCell>
          <TableCell sx={{ fontWeight: 600 }}>Listenpreis (brutto)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.rows.map((r) => (
          <TableRow key={r.art} sx={r.art === 'vorlaeufig' || r.art === 'offen' ? { '& td': { color: 'text.secondary' } } : undefined}>
            <TableCell sx={{ fontWeight: 600 }}>{ART[r.art]}</TableCell>
            <TableCell align="right">{formatNumber(r.anfos, 0)}</TableCell>
            <TableCell align="right">{formatPct(data.anfos > 0 ? (r.anfos / data.anfos) * 100 : null)}</TableCell>
            <TableCell>{preisText(r)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, maxWidth: '68ch' }}>
      {/* Gemessen 20.10.2026: Kunden mit Preis 374,40 € zahlen 374,40 € brutto / 349,91 € netto — der hinterlegte Preis ist
          brutto, die MwSt. hängt am Kunden (meist 7 %, teils 0 % oder 10 %). Audit B6d (30.10.): die Regel statt eines fest
          eingetragenen Preisbeispiels, das zu keinem Termin passte. */}
      Listenpreis der Werbeaktion zum Bestelltag, brutto inklusive MwSt. (netto = brutto ÷ 1,07 im Inland), und
      nur wenn alle Anfos der Art denselben tragen. Gezahlte Beträge zeigt die Sektion netto.
      <InfoTip text="Die Montagsmail zählt vorläufig ergänzte Anfos noch in Upsell und Quartalsbezug; hier stehen sie in einer eigenen Zeile." />
    </Typography>
  </Box>
);

export default BezugsartTabelle;
